/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";

import styles from "./index.module.scss";
import { RiDraggable } from "react-icons/ri";

import { DndContext, useDroppable, useDraggable } from "@dnd-kit/core";
import RouteCard, { RouteStop } from "Components/Cards/RouteCard";
import ContentContainer from "Components/Container/ContentContainer";
import {
  BackButton,
  Button,
  Description,
  SubHeader,
} from "Components/CustomComponents";
import { useUpdateRouteMutation } from "Data/services/routesApi";
import { getErrorMessage } from "Hooks/useErrorHandler";
import { ErrorToast, SuccessToast } from "Hooks/useToast";
import { RouteForm } from "Pages/CreateRoute/createRoute";
import { Container, Spinner } from "react-bootstrap";
import { FieldValues, useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { uniqueId } from "lodash";

const EditRoute: React.FC = () => {
  const location = useLocation();
  const routeDetails = location.state?.routeDetails;
  // console.info(routeDetails);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RouteForm | FieldValues>({
    defaultValues: {
      route_name: routeDetails.route_name || "",
      route_stops: routeDetails.route_stops?.map((item: RouteStop) => ({ ...item, latitude: parseFloat(item?.latitude) || null, longitude: parseFloat(item?.longitude) || null })) || [], // Array to hold multiple stops
    },
  });

  const watchStops = watch("route_stops");
  // console.log(watchStops);

  const navigate = useNavigate();

  const [draggingId, setDraggingId] = useState<number | string | null>(0);

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (errors.route_name?.message) {
      ErrorToast(errors.route_name.message as string);
    }
  }, [errors.route_name?.message, errors.route_stops?.message]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "route_stops",
  });

  const [updateRoute, { isLoading }] = useUpdateRouteMutation();

  const updatePriorities = (stops: RouteStop[]) => {
    return stops.map((stop: RouteStop, index: number) => ({
      ...stop,
      priority: index + 1,
    }));
  };

  const onSubmit = async (data: RouteForm | FieldValues) => {
    // console.log("before payload:", data);
    // console.log("stops:", stops);
    // data.route_stops = stops;
    // console.log("Final Payload:", data);

    try {
      const updatedData = await updatePriorities(data.route_stops);
      // console.log(updatedData);

      const response = await updateRoute({ id, updatedData: { route_stops: updatedData } }).unwrap();

      if (response.code !== 200) {
        ErrorToast(response.message);
      }

      if (response.code === 200) {
        navigate(-1);
        SuccessToast(response.message);
      }
    } catch (error: unknown) {
      // console.log("Login Error:", error);
      ErrorToast(getErrorMessage(error));
    }
  };

  const addStop = (isOneTime = false) => {
    setValue("route_stops", [
      ...watchStops,
      {
        slug: fields.length + 1,
        customer_name: "",
        customer_location: "",
        latitude: null,
        longitude: null,
        phone_number: "",
        customer_notes: "",
        is_one_time_stop: isOneTime ? 1 : undefined,
        priority: fields.length + 1,
      },
    ]);
  };

  const moveStop = (fromIndex: number, toIndex: number) => {
    const updatedStops = [...watchStops];
    const [movedStop] = updatedStops.splice(fromIndex, 1);
    updatedStops.splice(toIndex, 0, movedStop);
    console.log("Updated Stops : ", updatedStops)
    setValue("route_stops", updatedStops);
  };

  return (
    <div className={styles.EditRoute}>
      <BackButton isRelative={true} text={"Edit Route"} classes="mb-3" />

      <ContentContainer classes={styles.container}>
        <Container className="mx-0">
          <SubHeader text={routeDetails?.route_name} classes="highlighted" />
          <Description text={"Stops can be rearranged using drag option"} />
          <DndContext
            onDragStart={(event) => {
              setDraggingId(event.active.id);
            }}
            onDragEnd={({ active, over }) => {
              // console.log(active);
              // console.log(over);
              if (!over) return; // Avoid errors if `over` is null

              const activeIndex = (active.id as unknown as string).split(
                "-"
              )[1] as unknown as number;
              const overIndex = (over.id as unknown as string).split(
                "-"
              )[1] as unknown as number;

              if (activeIndex !== overIndex) {
                // console.log(activeIndex);
                // console.log(overIndex);
                moveStop(activeIndex, overIndex);
              }

              setDraggingId(null);
            }}
          >
            <div className="d-flex flex-column gap-2 my-3">
              {watchStops.map((stop: RouteStop, index: number) => (
                <DroppableContainer key={stop.id || stop.slug} index={index}>
                  <DraggableContentContainer index={index}>
                    <RouteCard
                      key={stop.id || stop.slug}
                      index={index}
                      isAccordian={true}
                      register={register}
                      control={control}
                      remove={remove}
                      stopData={stop as RouteStop}
                      setValue={setValue}
                    />
                  </DraggableContentContainer>
                </DroppableContainer>
              ))}
            </div>
          </DndContext>
          <div
            className={`${styles.buttonContainer} d-flex flex-md-row flex-column align-items-center justify-content-between gap-4`}
          >
            <div className="w-100 d-flex flex-sm-row flex-column align-items-center gap-2">
              <Button
                text={"Add New Stop"}
                onClick={() => addStop(false)}
                classes={`${styles.button} darkBlueBg `}
              />
              <Button
                text={"Add One Time Stop"}
                onClick={() => addStop(true)}
                classes={`${styles.button} transparentBtn`}
              />
            </div>
            <Button
              text={
                isLoading ? (
                  <Spinner animation="border" variant="light" size="sm" />
                ) : (
                  "Save & Change"
                )
              }
              onClick={handleSubmit(onSubmit)}
              classes={`${styles.button} ${styles.submit} royalBlueBg`}
            />
          </div>
        </Container>
      </ContentContainer>
    </div>
  );
};

const DroppableContainer = ({
  children,
  index,
}: {
  children: React.ReactNode;
  index: number | string;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `drop-${index}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={`${styles.dropTarget} ${isOver ? styles.dropTargetActive : ""
        }`}
    >
      {children}
    </div>
  );
};

const DraggableContentContainer = ({
  children,
  index,
}: {
  children: React.ReactNode;
  index: number;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `quiz-${index}`,
    data: { index },
  });

  return (
    <div
      ref={setNodeRef}
      // {...listeners}
      {...attributes}
      className={`${styles.stops} ${isDragging ? styles.dragging : ""
        } d-flex gap-2`}
    >
      <div
        {...listeners}
        className={`${styles.dragIcon}`}
      // className="p-1"
      >
        <RiDraggable />
      </div>

      {children}

      <div {...listeners} className="p-1"></div>
    </div>
  );
};

export default EditRoute;
